import { useState, useEffect, useRef, TouchEvent } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { cn } from '@/utils/cn';
import Image from '@/components/common/Image';
import { Button } from '@/components/common/Button';

interface TestimonialItem {
  quote: string;
  author: {
    name: string;
    title: string;
    image: string;
  };
  backgroundImage?: string;
  company?: {
    name: string;
    logo?: string;
  };
  logo?: string;
  actionUrl?: string;
}

interface TestimonialCarouselProps {
  testimonials: TestimonialItem[];
  className?: string;
  backgroundColor?: string;
  textColor?: string;
  showActionButton?: boolean;
  actionButton?: {
    text: string;
  };
  impactSection?: {
    tag: string;
    title: string;
  };
}

export const TestimonialCarousel: React.FC<TestimonialCarouselProps> = ({
  testimonials,
  className,
  backgroundColor = 'bg-black',
  textColor = 'text-white',
  showActionButton = false,
  actionButton = { text: 'VIEW SUCCESS STORY' },
  impactSection
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const slideDirectionRef = useRef<string>('');
  const touchStartXRef = useRef<number | null>(null);

  // Check if screen is mobile sized
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Add the slide animations as a style element in the document head
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.innerHTML = `
      @keyframes slideLeft {
        0% { transform: translateX(0); opacity: 1; }
        45% { transform: translateX(-10%); opacity: 0; }
        55% { transform: translateX(10%); opacity: 0; }
        100% { transform: translateX(0); opacity: 1; }
      }

      @keyframes slideRight {
        0% { transform: translateX(0); opacity: 1; }
        45% { transform: translateX(10%); opacity: 0; }
        55% { transform: translateX(-10%); opacity: 0; }
        100% { transform: translateX(0); opacity: 1; }
      }

      .slide-left {
        animation: slideLeft 600ms ease-in-out;
      }

      .slide-right {
        animation: slideRight 600ms ease-in-out;
      }
    `;
    document.head.appendChild(styleEl);

    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

  const nextTestimonial = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    slideDirectionRef.current = 'slide-left';
    setAnimationKey(prev => prev + 1); // Force a rerender with a new key

    setTimeout(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
      setTimeout(() => setIsTransitioning(false), 50);
    }, 300);
  };

  const previousTestimonial = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    slideDirectionRef.current = 'slide-right';
    setAnimationKey(prev => prev + 1); // Force a rerender with a new key

    setTimeout(() => {
      setCurrentIndex((prevIndex) => (prevIndex - 1 + testimonials.length) % testimonials.length);
      setTimeout(() => setIsTransitioning(false), 50);
    }, 300);
  };

  const jumpToTestimonial = (index: number) => {
    if (isTransitioning || index === currentIndex) return;
    setIsTransitioning(true);
    slideDirectionRef.current = index > currentIndex ? 'slide-left' : 'slide-right';
    setAnimationKey(prev => prev + 1); // Force a rerender with a new key

    setTimeout(() => {
      setCurrentIndex(index);
      setTimeout(() => setIsTransitioning(false), 50);
    }, 300);
  };

  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    touchStartXRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
    if (touchStartXRef.current === null) return;

    const touchEndX = e.changedTouches[0].clientX;
    const diffX = touchEndX - touchStartXRef.current;

    // If swipe distance is significant enough (more than 50px)
    if (Math.abs(diffX) > 50) {
      if (diffX > 0) {
        // Swipe right - go to previous
        previousTestimonial();
      } else {
        // Swipe left - go to next
        nextTestimonial();
      }
    }

    touchStartXRef.current = null;
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section
      className={cn(`py-16 md:py-24 ${backgroundColor} ${textColor} relative group`, className)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Impact Section Header */}
      {impactSection && (
        <div className="container mx-auto px-4 mb-8">
          <div className="text-center max-w-4xl mx-auto">
            <span className="inline-block text-sm font-semibold text-blue-400 tracking-wider mb-2 md:mb-4">
              {impactSection.tag}
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold">
              {impactSection.title}
            </h2>
          </div>
        </div>
      )}

      {/* Navigation Arrows - Hidden on Mobile */}
      {!isMobile && (
        <>
          <button
            onClick={previousTestimonial}
            className={`absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full
            bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border border-white/20
            transition-all duration-200 transform shadow-md
            ${isHovered ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
            hidden md:flex md:items-center md:justify-center`}
            aria-label="Previous testimonial"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>

          <button
            onClick={nextTestimonial}
            className={`absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full
            bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border border-white/20
            transition-all duration-200 transform shadow-md
            ${isHovered ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
            hidden md:flex md:items-center md:justify-center`}
            aria-label="Next testimonial"
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </>
      )}

      <div className="container mx-auto px-4 h-auto min-h-[500px] md:h-[550px] lg:h-[600px] overflow-hidden">
        {/* Mobile Layout */}
        {isMobile ? (
          <div
            key={animationKey}
            className={`flex flex-col h-full ${slideDirectionRef.current}`}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {/* Image at the top */}
            <div className="w-full aspect-[4/3] rounded-lg overflow-hidden mb-6 max-h-[30%]">
              <div className="absolute inset-0 bg-gradient-to-br from-primary-600/20 to-transparent mix-blend-overlay" />
              <Image
                src={currentTestimonial.backgroundImage || currentTestimonial.author.image}
                alt="Testimonial background"
                width={800}
                height={600}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Logo if available */}
            {currentTestimonial.logo && (
              <div className="mb-4">
                <Image
                  src={currentTestimonial.logo}
                  alt="Company logo"
                  width={120}
                  height={30}
                  className="h-8 w-auto"
                />
              </div>
            )}

            {/* Company Logo/Name if available */}
            {!currentTestimonial.logo && currentTestimonial.company?.name && (
              <div className="mb-4">
                {currentTestimonial.company.logo ? (
                  <Image
                    src={currentTestimonial.company.logo}
                    alt={currentTestimonial.company.name}
                    width={100}
                    height={30}
                    className="h-8 w-auto"
                  />
                ) : (
                  <p className="text-lg font-semibold">{currentTestimonial.company.name}</p>
                )}
              </div>
            )}

            {/* Quote */}
            <div className="mb-6 flex-grow overflow-y-auto">
              <blockquote>
                <p className="text-lg leading-relaxed">
                  "{currentTestimonial.quote}"
                </p>
              </blockquote>
            </div>

            {/* Author Details */}
            <div className="mb-4">
              <h3 className="font-semibold text-sm">{currentTestimonial.author.name}</h3>
              <p className={cn("text-sm text-gray-400", textColor)}>{currentTestimonial.author.title}</p>
            </div>

            {/* Action Button (Optional) */}
            {showActionButton && currentTestimonial.actionUrl && (
              <div className="mb-4">
                <Button
                  variant="solid"
                  color="primary"
                  className="w-full"
                  onClick={() => window.open(currentTestimonial.actionUrl, '_blank')}
                >
                  {actionButton.text}
                </Button>
              </div>
            )}
          </div>
        ) : (
          /* Desktop Layout */
          <div key={animationKey} className={`grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 items-center h-full ${slideDirectionRef.current}`}>
            {/* Content */}
            <div className="space-y-6 md:space-y-8 h-full flex flex-col justify-center">
              {/* Logo if available */}
              {currentTestimonial.logo && (
                <div className="mb-2">
                  <Image
                    src={currentTestimonial.logo}
                    alt="Company logo"
                    width={150}
                    height={40}
                    className="h-10 w-auto"
                  />
                </div>
              )}

              {/* Quote */}
              <blockquote className="max-h-[300px] overflow-y-auto">
                <p className="text-lg md:text-xl lg:text-2xl font-medium leading-relaxed">
                  "{currentTestimonial.quote}"
                </p>
              </blockquote>

              {/* Author */}
              <div className="flex flex-col gap-1">
                <h3 className="font-semibold text-sm md:text-base">{currentTestimonial.author.name}</h3>
                <p className={cn("text-sm text-gray-400", textColor)}>{currentTestimonial.author.title}</p>
              </div>

              {/* Action Button (Optional) */}
              {showActionButton && currentTestimonial.actionUrl && (
                <div className="mt-6">
                  <Button
                    variant="solid"
                    color="primary"
                    onClick={() => window.open(currentTestimonial.actionUrl, '_blank')}
                  >
                    {actionButton.text}
                  </Button>
                </div>
              )}
            </div>

            {/* Image */}
            <div className="relative aspect-[4/3] rounded-lg overflow-hidden mt-6 md:mt-0 max-h-[400px]">
              <div className="absolute inset-0 bg-gradient-to-br from-primary-600/20 to-transparent mix-blend-overlay" />
              <Image
                src={currentTestimonial.backgroundImage || currentTestimonial.author.image}
                alt="Testimonial background"
                width={800}
                height={600}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        )}
      </div>

      {/* Dots Indicator */}
      <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 mt-4">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => jumpToTestimonial(index)}
            className={`h-2 md:h-2 rounded-full transition-all duration-200 ${
              currentIndex === index
                ? 'bg-white w-6 md:w-4'
                : 'bg-white/60 w-2'
            }`}
            aria-label={`Go to testimonial ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};